import { addMinutes } from 'date-fns/addMinutes';
import { differenceInDays } from 'date-fns/differenceInDays';
import { differenceInHours } from 'date-fns/differenceInHours';
import { differenceInMinutes } from 'date-fns/differenceInMinutes';
import { isValid } from 'date-fns/isValid';
import { parseISO } from 'date-fns/parseISO';
import { subDays } from 'date-fns/subDays';
import { subHours } from 'date-fns/subHours';
import { formatDuration } from 'date-fns/formatDuration';
import { Duration } from 'date-fns/types';
import { format } from 'date-fns/format';
import { intlFormat } from 'date-fns/intlFormat';

const i18nDurationDay: Record<string, Record<string, Record<string, string>>> = {
  multiple: {
    long: {
      de: 'Tage',
      en: 'Days',
      fr: 'Jours',
      it: 'Giorni',
    },
  },
  single: {
    long: {
      de: 'Tag',
      en: 'Day',
      fr: 'Jour',
      it: 'Giorno',
    },
  },
};

const i18nDurationMinute: Record<string, Record<string, Record<string, string>>> = {
  multiple: {
    long: {
      de: 'Minuten',
      en: 'Minutes',
      fr: 'Minutes',
      it: 'Minuti',
    },
    short: {
      de: 'Min',
      en: 'min',
      fr: 'min.',
      it: 'mins',
    },
  },
  single: {
    long: {
      de: 'Minute',
      en: 'Minute',
      fr: 'Minute',
      it: 'Minuto',
    },
    short: {
      de: 'm',
      en: 'm',
      fr: 'm',
      it: 'm',
    },
  },
};

const i18nDurationHour: Record<string, Record<string, Record<string, string>>> = {
  multiple: {
    long: {
      de: 'Stunden',
      en: 'Hours',
      fr: 'Heures',
      it: 'Ore',
    },
    short: {
      de: 'Std.',
      en: 'h',
      fr: 'ore',
      it: 'hrs',
    },
  },
  single: {
    long: {
      de: 'Stunde',
      en: 'Hour',
      fr: 'Heure',
      it: 'Ora',
    },
    short: {
      de: 'Std.',
      en: 'h',
      fr: 'ore',
      it: 'hrs',
    },
  },
};

/**
 * This function removes the offset of a ISO date string. This needs to
 * be done to make sure that the offset is ignored.
 * If it is not removed the time would be converted to the
 * browsers local time. Example: "2022-08-29T09:30:00+03:00" would be changed to
 * "2022-08-29T08:30:00+02:00" --> not the desired outcome, as we want to use
 * the time in the timezone and not the local time.
 *
 * After the offset is removed a new date is created in the browsers local time.
 * This ensures that the time of the ISO date string is displayed and not a
 * converted time. Example: "2022-08-29T09:30:00+03:00" would be changed to
 * "2022-08-29T09:30:00+02:00" --> desired outcome
 * Attention: This function does not convert the time to the correct time in the
 * local timezone. It is a workaround to make sure, that the given time is
 * displayed without being converted.
 *
 * Offset: Difference between the timezone and Coordinated Universal Time.
 *
 * @param isoTime - the iso time with the timezone offset
 * @returns a new date with the local timezone with the local offset
 */
export const removeTimezoneFromIsoTimeString = (isoTime?: string): Date | undefined => {
  if (!isoTime) {
    return undefined;
  }

  const parsedDate = parseISO(isoTime);

  if (!isValid(parsedDate)) {
    return undefined;
  }

  if (isoTime.includes('Z')) {
    return new Date(isoTime.replace('Z', ''));
  }

  if (isoTime.includes('T')) {
    const dateTime = isoTime.split('T');

    if (dateTime[1] && (dateTime[1].includes('+') || dateTime[1].includes('-'))) {
      return new Date(`${dateTime[0]}T${dateTime[1].split(/[+-]/u)[0]}`);
    }
  }

  return new Date(isoTime);
};

export const durationToTime = (
  duration: number,
  currentLanguage?: string,
): { short: string; long: string } => {
  const short = [];
  const long = [];

  const now = 0;
  let future = addMinutes(now, duration);

  const days = differenceInDays(future, now);

  if (days > 0) {
    short.push(`${days} d`);

    if (currentLanguage) {
      long.push(`${days} ${
        days > 1
          ? i18nDurationDay.multiple.long[currentLanguage]
          : i18nDurationDay.single.long[currentLanguage]
      }`);
    }
    future = subDays(future, days);
  }

  const hours = differenceInHours(future, now);

  if (hours > 0) {
    short.push(`${hours} h`);

    if (currentLanguage) {
      long.push(`${hours} ${
        hours > 1
          ? i18nDurationHour.multiple.long[currentLanguage]
          : i18nDurationHour.single.long[currentLanguage]
      }`);
    }

    future = subHours(future, hours);
  }

  const minutes = differenceInMinutes(future, now);

  if (minutes > 0) {
    short.push(`${minutes} min`);

    if (currentLanguage) {
      long.push(`${minutes} ${
        minutes > 1
          ? i18nDurationMinute.multiple.long[currentLanguage]
          : i18nDurationMinute.single.long[currentLanguage]
      }`);
    }
  }

  return {
    long: long.join(' '),
    short: short.join(' '),
  };
};

const isoDurationToDurationObject = (duration: string): Duration => {
  // We get iso duration like: P1DT1H6M
  // and want to convert it to a date-fns duration object

  /* eslint-disable */
  const ISO_DURATION_REGEX = /P(?:([\d]+\.?[\d]*|\.[\d]+)Y)?(?:([\d]+\.?[\d]*|\.[\d]+)M)?(?:([\d]+\.?[\d]*|\.[\d]+)W)?(?:([\d]+\.?[\d]*|\.[\d]+)D)?(?:T(?:([\d]+\.?[\d]*|\.[\d]+)H)?(?:([\d]+\.?[\d]*|\.[\d]+)M)?(?:([\d]+\.?[\d]*|\.[\d]+)S)?)?$/;
  const parsedArgument = duration.replace(/,/g, ".");
  /* eslint-enable */

  const match = parsedArgument.match(ISO_DURATION_REGEX);

  if (!match) {
    throw new RangeError('Invalid format');
  }

  const [
    ,
    years = 0,
    months = 0,
    weeks = 0,
    days = 0,
    hours = 0,
    minutes = 0,
    seconds = 0,
  ] = match;

  /* eslint-disable sort-keys */
  const entries = Object.entries({
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
  });
  /* eslint-enable sort-keys */

  const durationObject = entries.reduce((prev, [
    key,
    value,
  ]) => {
    /* eslint-disable no-implicit-coercion */
    prev[key] = +value;
    /* eslint-enable no-implicit-coercion */

    return prev;
  }, {});

  return durationObject;
};

export interface InterfaceDateHelperTripDuration {
  format: 'long' | 'short';
}

export const isoDurationToTripDuration = (duration: string, formatInterface: InterfaceDateHelperTripDuration['format'], language: string): string => {
  if (duration === undefined) {
    return '';
  }

  const durationObject = isoDurationToDurationObject(duration);

  const formatDistanceLocaleShort = {
    xDays: '{{count}} d',
    xHours: '{{count}} h',
    xMinutes: '{{count}} min',
  };

  const formatDistanceLocaleLong = {
    xDays: `{{count}} ${i18nDurationDay.multiple.long[language]}`,
    xHours: `{{count}} ${i18nDurationHour.multiple.long[language]}`,
    xMinutes: `{{count}} ${i18nDurationMinute.multiple.long[language]}`,
  };

  const formatDistanceLocaleLongSingle = {
    xDays: `{{count}} ${i18nDurationDay.multiple.long[language]}`,
    xHours: `{{count}} ${i18nDurationHour.single.long[language]}`,
    xMinutes: `{{count}} ${i18nDurationMinute.single.long[language]}`,
  };

  const shortLocale = {
    formatDistance: (token, count): any => {
      let formatter;

      if (formatInterface === 'short') {
        formatter = formatDistanceLocaleShort[token].replace('{{count}}', count);
      } else {
        if (count === 1) {
          formatter = formatDistanceLocaleLongSingle[token].replace('{{count}}', count);
        } else {
          formatter = formatDistanceLocaleLong[token].replace('{{count}}', count);
        }
      }

      return formatter;
    },
  };

  return formatDuration(durationObject, {
    delimiter: ' ',
    format: [
      'days',
      'hours',
      'minutes',
    ],
    locale: shortLocale,
    zero: false,
  });
};

export const isoDurationToWalkDuration = (duration: string): number => {
  const durationObject = isoDurationToDurationObject(duration);

  const {
    days,
  } = durationObject;
  const hours = durationObject.hours + (days * 24);
  const minutes = durationObject.minutes + (hours * 60);

  return minutes;
};

export const isoDurationToWalkDurationFormatted = (
  duration: string,
  language: string,
): string => {
  const minutes = isoDurationToWalkDuration(duration);

  if (minutes === 0) {
    return '';
  }

  if (minutes === 1) {
    return `${minutes} ${i18nDurationMinute.single.long[language]}`;
  }

  return `${minutes} ${i18nDurationMinute.multiple.long[language]}`;
};

// e.g: from 2024-11-28 to 28.11.2024
export const formatDateStringToReadableDate = (date: string): string => {
  const dateObject = new Date(date);

  return format(dateObject, 'dd.MM.yyyy');
};

export const formatDateStringToReadableDateLongForm = (date: string, language: string): string => intlFormat(new Date(date), {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
}, {
  locale: `${language}-${language.toUpperCase()}`,
});

// e.g: Sun Nov 17 2024 09:25:53 GMT+0100 to 2024-11-27
export const formatDateForDatepicker = (date: Date): string => format(date, 'yyyy-MM-dd');

// e.g: Sun Nov 17 2024 09:25:53 GMT+0100 to 09:25
export const formatDateForTimepicker = (date: Date): string => format(date, 'HH:mm');

// e.g.: 16.12.2024 to Mo, 16.12.2024
export const formatDateStringIncludingDayName = (date: string, lang: string): string => {
  const formattedDate = format(new Date(date), 'dd.MM.yyyy');
  const weekday = intlFormat(new Date(date), {
    weekday: 'long',
  }, {
    locale: `${lang}-${lang.toUpperCase()}`,
  });

  return `${weekday}, ${formattedDate}`;
};
